import React from 'react';
import { LoadingSpinner } from '../LoadingSpinner';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    isInitializing: boolean;
    hasPermission: boolean | null;
    onRetry: () => void;
}

export function MicrophoneModal({ isOpen, onClose, isInitializing, hasPermission, onRetry }: Props) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl transform transition-all">
                <div className="p-6 relative">
                    <div className="absolute top-4 right-4">
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                        <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        Microphone Access Required
                    </h2>
                    
                    <div className="space-y-4">
                        {isInitializing ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <div className="relative mb-6">
                                    <div className="animate-ping absolute h-12 w-12 rounded-full bg-primary/30"></div>
                                    <LoadingSpinner size="lg" className="text-primary dark:text-primary/90 relative" />
                                </div>
                                <p className="text-base text-gray-700 dark:text-gray-300 text-center font-medium mb-1">
                                    Initializing microphone...
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                                    Your browser will ask for permission to access your microphone.
                                    <br />Please click "Allow" when prompted.
                                </p>
                            </div>
                        ) : hasPermission === false ? (
                            <div className="space-y-5">
                                <div className="flex items-start gap-3 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-4 rounded-lg">
                                    <svg className="w-6 h-6 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <div>
                                        <p className="font-medium">Microphone access denied</p>
                                        <p className="text-sm mt-1 text-red-500 dark:text-red-300">
                                            This app requires microphone access to record audio.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                                    <h3 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">How to fix this:</h3>
                                    <ol className="list-decimal pl-5 text-sm text-gray-600 dark:text-gray-400 space-y-2">
                                        <li>Click the lock/info icon in your browser's address bar</li>
                                        <li>Find "Microphone" in the site permissions</li>
                                        <li>Change the setting from "Block" to "Allow"</li>
                                        <li>Refresh the page or click "Try Again" below</li>
                                    </ol>
                                </div>
                                
                                <div className="flex flex-col space-y-3 mt-6">
                                    <button
                                        onClick={onRetry}
                                        className="w-full px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors shadow-sm"
                                    >
                                        Try Again
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                                    >
                                        Continue without Microphone
                                    </button>
                                </div>
                            </div>
                        ) : hasPermission === true ? (
                            <div className="flex flex-col items-center justify-center py-6">
                                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-5 animate-scale-in">
                                    <svg className="w-8 h-8 text-green-500 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <p className="text-base text-gray-800 dark:text-gray-200 text-center font-medium mb-1">
                                    Microphone access granted
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-5">
                                    You can now start recording audio.
                                </p>
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors shadow-sm"
                                >
                                    Continue
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
